import React, { useState, useEffect, useRef } from 'react';
import { get, some, values, sortBy, isEmpty } from 'lodash';
import { Container } from 'react-bootstrap';
import Header from './Header';
import HostPanel from './HostPanel';
import Leaderboard from './Leaderboard';
import QuestionValue, { StatPill } from './QuestionValue';
import Podium from './Podium';
import QueueList from './QueueList';
import { buzzFeedback, unlockAudio } from '../lib/buzzFeedback';
import { fireConfetti, fireFinaleConfetti } from '../lib/confetti';

export default function Table(game) {
  const [loaded, setLoaded] = useState(false);
  const [buzzed, setBuzzer] = useState(
    some(game.G.queue, (o) => o.id === game.playerID)
  );
  const [lastBuzz, setLastBuzz] = useState(null);
  const [soundPlayed, setSoundPlayed] = useState(false);
  const [flash, setFlash] = useState(null);
  const buzzButton = useRef(null);
  const queueRef = useRef(null);
  const lastEventAt = useRef(null);
  const clearedFinishedAuth = useRef(false);
  const finishedConfetti = useRef(false);
  const buzzSentRef = useRef(false);
  const pointerHandledRef = useRef(false);
  const canBuzzRef = useRef(false);
  const gameRef = useRef(game);
  gameRef.current = game;

  const playFirstBuzzFx = (withStrongVibrate) => {
    if (soundPlayed) return;
    buzzFeedback({
      sound: true,
      vibrate: true,
      strong: withStrongVibrate,
    });
    setSoundPlayed(true);
  };

  // Yakuniy podium: sessiyani SAQLAYMIZ — restart bilan shu xonada qolish uchun.
  // Faqat confetti bir marta.
  useEffect(() => {
    if (game.G.phase === 'finished') {
      if (!finishedConfetti.current) {
        finishedConfetti.current = true;
        fireFinaleConfetti();
      }
      return;
    }
    // Lobby/playing ga qaytganda (restart) confetti flagini tiklash
    if (game.G.phase === 'lobby' || game.G.phase === 'playing') {
      finishedConfetti.current = false;
      clearedFinishedAuth.current = false;
    }
  }, [game.G.phase]);

  useEffect(() => {
    if (!game.G.queue[game.playerID]) {
      buzzSentRef.current = false;
      if (lastBuzz && Date.now() - lastBuzz < 500) {
        setTimeout(() => {
          const queue = queueRef.current;
          if (queue && !queue[game.playerID]) {
            setBuzzer(false);
          }
        }, 500);
      } else {
        setBuzzer(false);
      }
    } else {
      buzzSentRef.current = true;
      setBuzzer(true);
    }

    if (isEmpty(game.G.queue)) {
      setSoundPlayed(false);
      buzzSentRef.current = false;
    } else if (loaded) {
      // Birinchi buzz — hammada yoqimli chime + yengil vibratsiya
      playFirstBuzzFx(false);
    }

    if (!loaded) {
      setLoaded(true);
    }

    queueRef.current = game.G.queue;
  }, [game.G.queue]);

  // Savol ochilganda local “yuborildi” flagni tozalash
  useEffect(() => {
    if (game.G.question.status === 'open' && !game.G.locked) {
      if (!game.G.queue[game.playerID]) {
        buzzSentRef.current = false;
      }
    }
  }, [game.G.question.status, game.G.locked, game.G.queue, game.playerID]);

  useEffect(() => {
    const ev = game.G.lastEvent;
    if (!ev || ev.at === lastEventAt.current) return;
    lastEventAt.current = ev.at;
    if (ev.type === 'correct') {
      setFlash('correct');
      // Faqat to‘g‘ri topgan jamoa ekranida
      if (String(ev.playerId) === String(game.playerID)) {
        fireConfetti({ durationMs: 3400, particleCount: 160 });
      }
    } else if (ev.type === 'incorrect') setFlash('incorrect');
    const t = setTimeout(() => setFlash(null), 900);
    return () => clearTimeout(t);
  }, [game.G.lastEvent, game.playerID]);

  const attemptBuzz = () => {
    const g = gameRef.current;
    if (!g || !g.isConnected) return;
    if (buzzSentRef.current) return;
    if (
      g.G.locked ||
      g.G.phase !== 'playing' ||
      g.G.question.status !== 'open'
    ) {
      return;
    }
    if (g.G.hostId && String(g.playerID) === String(g.G.hostId)) return;
    if (g.G.queue[g.playerID]) return;

    // Avval move — ovoz/vibratsiya keyin (kechiktirishsiz)
    buzzSentRef.current = true;
    setBuzzer(true);
    setLastBuzz(Date.now());
    try {
      g.moves.buzz(g.playerID);
    } catch (e) {
      buzzSentRef.current = false;
      setBuzzer(false);
      return;
    }
    // Feedback asinxron — main thread / move’ni bloklamaydi
    setTimeout(() => {
      unlockAudio();
      playFirstBuzzFx(true);
    }, 0);
  };

  const onBuzzPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!canBuzzRef.current) return;
    pointerHandledRef.current = true;
    if (e.cancelable) e.preventDefault();
    attemptBuzz();
  };

  const onBuzzClick = () => {
    // Faqat pointerdown bo‘lmagan holat (keyboard / ba’zi brauzerlar)
    if (pointerHandledRef.current) {
      pointerHandledRef.current = false;
      return;
    }
    if (!canBuzzRef.current) return;
    attemptBuzz();
  };

  useEffect(() => {
    function onKeydown(e) {
      if ((e.code === 'Space' || e.keyCode === 32) && !e.repeat) {
        e.preventDefault();
        if (canBuzzRef.current) attemptBuzz();
      }
    }
    window.addEventListener('keydown', onKeydown);
    return () => window.removeEventListener('keydown', onKeydown);
  }, []);

  const players = !game.gameMetadata
    ? []
    : game.gameMetadata
        .filter((p) => p.name)
        .map((p) => ({ ...p, id: String(p.id) }));

  const firstPlayer =
    get(
      sortBy(players, (p) => parseInt(p.id, 10)).filter((p) => p.connected),
      '0'
    ) || null;

  const hostId = game.G.hostId || get(firstPlayer, 'id');
  const isHost = hostId === game.playerID;
  const teams = players.filter((p) => p.id !== hostId);

  const queue = sortBy(values(game.G.queue), ['timestamp']);
  const buzzedPlayers = queue
    .map((p) => {
      const player = teams.find((t) => t.id === p.id);
      if (!player) return {};
      return { ...p, name: player.name, connected: player.connected };
    })
    .filter((p) => p.name);

  const headPlayer = buzzedPlayers[0] || null;
  const myQueueIndex = buzzedPlayers.findIndex((p) => p.id === game.playerID);

  const myScore =
    game.G.scores[game.playerID] !== undefined
      ? game.G.scores[game.playerID]
      : game.G.config.startingScore;

  const canBuzz =
    !isHost &&
    !buzzed &&
    !buzzSentRef.current &&
    !game.G.locked &&
    game.G.phase === 'playing' &&
    game.G.question.status === 'open' &&
    !!game.isConnected;

  canBuzzRef.current = canBuzz;

  let buzzLabel = 'Men';
  if (game.G.phase === 'lobby') buzzLabel = 'Kutilmoqda';
  else if (game.G.phase === 'finished') buzzLabel = 'Tugadi';
  else if (game.G.locked || game.G.question.status === 'resolved')
    buzzLabel = 'Yopiq';
  else if (myQueueIndex === 0) buzzLabel = 'Javob!';
  else if (buzzed) buzzLabel = 'Navbatda';

  const showWave =
    buzzLabel === 'Men' || buzzLabel === 'Javob!' || buzzLabel === 'Navbatda';

  let hostConfig = game.headerData && game.headerData.hostConfig;
  if (!hostConfig && game.gameID) {
    try {
      const raw = sessionStorage.getItem(`zakbuzz_host_config_${game.gameID}`);
      if (raw) hostConfig = JSON.parse(raw);
    } catch (e) {
      hostConfig = null;
    }
  }

  const showRoundLeaderboard =
    game.G.phase === 'lobby' ||
    (game.G.phase === 'playing' && game.G.question.status === 'resolved');

  const isRoundBreak =
    game.G.phase === 'playing' && game.G.question.status === 'resolved';

  const lobbyScores = Object.fromEntries(
    teams.map((t) => [t.id, game.G.config.startingScore])
  );

  const leaderboardBlock =
    showRoundLeaderboard && teams.length > 0 ? (
      <div className={`host-lb ${isRoundBreak ? 'round-break-lb' : ''}`}>
        <Leaderboard
          players={teams}
          scores={game.G.phase === 'lobby' ? lobbyScores : game.G.scores}
          highlightId={isHost ? null : game.playerID}
          limit={isHost ? null : 12}
          variant={isRoundBreak ? 'scoreboard' : 'default'}
          afterQuestion={isRoundBreak ? game.G.question.number : null}
          title={game.G.phase === 'lobby' ? 'Jamoalar' : 'Natijalar'}
        />
      </div>
    ) : null;

  function confirmEndGame() {
    if (
      window.confirm(
        'O‘yinni yakunlashni xohlaysizmi? Bu amalni qaytarib bo‘lmaydi.'
      )
    ) {
      game.moves.endGame();
    }
  }

  function confirmRestart() {
    if (
      window.confirm(
        'Shu xonada yangi o‘yin? Jamoalar saqlanadi, ballar noldan boshlanadi.'
      )
    ) {
      game.moves.restartMatch(
        game.G.config.startingScore,
        game.G.config.penalty
      );
    }
  }

  const canRestartHeader =
    isHost &&
    game.G.phase === 'playing' &&
    game.G.question.status === 'resolved';

  let playerStatus = 'Men bosing';
  if (game.G.phase === 'lobby') playerStatus = 'O‘yin boshlanishini kuting';
  else if (game.G.phase === 'finished') playerStatus = 'O‘yin yakunlandi';
  else if (game.G.question.status === 'resolved')
    playerStatus = 'Keyingi savol kutilmoqda';
  else if (myQueueIndex === 0) playerStatus = 'Siz javob bering!';
  else if (myQueueIndex > 0 && headPlayer)
    playerStatus = `${headPlayer.name} javob beryapti · siz #${
      myQueueIndex + 1
    }`;
  else if (headPlayer) playerStatus = `${headPlayer.name} javob beryapti`;
  else if (canBuzz) playerStatus = 'Men bosing';
  else playerStatus = 'Ovoz kutilyapti';

  return (
    <div className={`game-shell ${flash || ''}`}>
      <Header
        isHost={isHost}
        roomID={game.gameID}
        canEndGame={game.G.phase === 'playing'}
        onEndGame={confirmEndGame}
        canRestart={canRestartHeader}
        onRestart={confirmRestart}
      />
      <Container className="game-body">
        <div className="game-topbar">
          {!isHost && game.G.phase !== 'finished' ? (
            <StatPill label="Jamoa bali" value={myScore} title="Jamoa bali" />
          ) : (
            <span className="topbar-spacer" aria-hidden="true" />
          )}
          {game.G.phase === 'finished' ||
          game.G.question.status === 'idle' ? (
            <span className="topbar-spacer" aria-hidden="true" />
          ) : (
            <QuestionValue
              value={game.G.question.value}
              questionNumber={game.G.question.number}
              status={game.G.question.status}
            />
          )}
        </div>

        {!game.isConnected ? (
          <p className="warning">
            Aloqa uzildi — qayta ulanilmoqda… Ballar saqlanadi, sahifani yopmang.
          </p>
        ) : null}

        {game.G.phase === 'finished' ? (
          <div className="finale-wrap">
            <Podium
              players={teams}
              scores={game.G.scores}
              roomID={game.gameID}
            />
            {isHost ? (
              <HostPanel
                game={game}
                headPlayer={null}
                buzzedPlayers={[]}
                hostConfig={hostConfig}
                teams={teams}
              />
            ) : (
              <p className="hint center-hint">
                Host yangi o‘yinni shu xonada boshlashi mumkin — sahifani yopmang.
              </p>
            )}
          </div>
        ) : isHost ? (
          <div className="host-view">
            {/* Savol oralig‘ida avval natijalar — host ko‘rsatib, keyin narx belgilaydi */}
            {isRoundBreak ? leaderboardBlock : null}

            <HostPanel
              game={game}
              headPlayer={headPlayer}
              buzzedPlayers={buzzedPlayers}
              hostConfig={hostConfig}
              teams={teams}
            />

            {game.G.phase === 'playing' &&
            game.G.question.status === 'open' ? (
              <QueueList
                buzzedPlayers={buzzedPlayers}
                isHost
                onReset={(id) => game.moves.resetBuzzer(id)}
              />
            ) : null}

            {!isRoundBreak ? leaderboardBlock : null}

            {game.G.phase === 'lobby' && teams.length === 0 ? (
              <p className="hint center-hint">
                Jamoalar qo‘shilishini kuting...
              </p>
            ) : null}
          </div>
        ) : (
          <div className={`player-view ${isRoundBreak ? 'is-break' : ''}`}>
            {isRoundBreak ? (
              <>
                <p className="player-status break-status">{playerStatus}</p>
                {leaderboardBlock}
              </>
            ) : (
              <>
                <p
                  className={`player-status ${
                    myQueueIndex === 0 ? 'active-turn' : ''
                  }`}
                >
                  {playerStatus}
                </p>

                <div className="buzzer-wrap">
                  <button
                    ref={buzzButton}
                    type="button"
                    className={`buzz-btn ${canBuzz ? 'is-ready' : ''} ${
                      buzzed || buzzSentRef.current ? 'is-buzzed' : ''
                    }`}
                    aria-disabled={!canBuzz}
                    onPointerDown={onBuzzPointerDown}
                    onClick={onBuzzClick}
                  >
                    <span className="buzz-word">{buzzLabel}</span>
                    {showWave ? (
                      <img
                        className="buzz-wave"
                        src={`${process.env.PUBLIC_URL}/wave-hand.png`}
                        alt=""
                        draggable="false"
                      />
                    ) : null}
                  </button>
                </div>

                {game.G.phase === 'playing' &&
                game.G.question.status === 'open' ? (
                  <QueueList
                    buzzedPlayers={buzzedPlayers}
                    myId={game.playerID}
                  />
                ) : null}

                {game.G.phase === 'lobby' ? leaderboardBlock : null}
              </>
            )}
          </div>
        )}
      </Container>
    </div>
  );
}
